// Plugin types come from Day.js; importing the initialized factory here would create a cycle.
// oxlint-disable-next-line no-restricted-imports
import type { Dayjs, OpUnitType, PluginFunc, QUnitType } from "dayjs"

export interface FiscalCalendar {
  readonly startMonth: number
  readonly numberOffset: number
}

type FiscalUnit = OpUnitType | QUnitType | "isoWeek" | "fiscalYear"

declare module "dayjs" {
  interface Dayjs {
    withFiscalCalendar(calendar: FiscalCalendar): Dayjs
    fiscalYear(): number
    startOf(unit: FiscalUnit, startOf?: boolean): Dayjs
    endOf(unit: FiscalUnit): Dayjs
  }
}

// Day.js preserves its extension data through cloning and calendar arithmetic.
type ExtendedDayjs = Dayjs & { $x: { fiscalCalendar?: FiscalCalendar } }

export const fiscalYear: PluginFunc = (_option, DayjsClass) => {
  const { prototype } = DayjsClass
  const originalStartOf: (unit: FiscalUnit, startOf?: boolean) => Dayjs = prototype.startOf
  const originalUtc = prototype.utc
  const originalLocal = prototype.local
  const originalTimezone = prototype.tz

  prototype.withFiscalCalendar = function withFiscalCalendar(calendar) {
    const date = this.clone() as ExtendedDayjs
    date.$x = { ...date.$x, fiscalCalendar: Object.freeze({ ...calendar }) }
    return date
  }

  prototype.startOf = function fiscalStartOf(unit: FiscalUnit, isStart?: boolean) {
    if (unit !== "fiscalYear")
      return retainCalendar(this, originalStartOf.call(this, unit, isStart !== false))

    const { startMonth } = calendarFor(this)
    const start = this.startOf("year").month(startMonth - 1)
    const boundary = (this.month() < startMonth - 1 ? start.subtract(1, "year") : start).startOf(
      "day"
    )
    return isStart !== false
      ? boundary
      : boundary.add(1, "year").startOf("day").subtract(1, "millisecond")
  }

  prototype.fiscalYear = function getFiscalYear() {
    return this.startOf("fiscalYear").year() + calendarFor(this).numberOffset
  }

  // UTC/local/timezone plugins construct new instances rather than always cloning $x.
  prototype.utc = function utc(keepLocalTime) {
    return retainCalendar(this, originalUtc.call(this, keepLocalTime))
  }
  prototype.local = function local() {
    return retainCalendar(this, originalLocal.call(this))
  }
  prototype.tz = function inTimezone(timezone, keepLocalTime) {
    return retainCalendar(this, originalTimezone.call(this, timezone, keepLocalTime))
  }
}

function calendarFor(date: Dayjs): FiscalCalendar {
  const calendar = (date as ExtendedDayjs).$x.fiscalCalendar
  if (!calendar) throw new Error("Fiscal calendar is not bound to this date")
  return calendar
}

function retainCalendar(source: Dayjs, result: Dayjs): Dayjs {
  const calendar = (source as ExtendedDayjs).$x.fiscalCalendar
  return calendar ? result.withFiscalCalendar(calendar) : result
}
